import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, MapPin, Phone, Mail, Clock } from "lucide-react";
import { SHOP_INFO } from "../constants/shopData";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const messageRef = useRef(null);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  useEffect(() => {
    const handler = (e) => {
      const msg = e?.detail?.message;
      if (!msg) return;
      setForm((f) => ({ ...f, message: msg }));
      setDone(false);
      setTimeout(() => {
        if (messageRef.current) {
          messageRef.current.focus();
          // Place cursor at end
          const len = messageRef.current.value.length;
          messageRef.current.setSelectionRange(len, len);
        }
      }, 350);
    };
    window.addEventListener("rcs:inquire", handler);
    return () => window.removeEventListener("rcs:inquire", handler);
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please add your name, email, and a message.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message sent! We'll reach out about your appointment.");
      setDone(true);
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not send right now.";
      toast.error(typeof msg === "string" ? msg : "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="bg-[#F4F4F5] border-b-4 border-black"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          <div className="lg:col-span-5">
            <span className="tag-chip" data-testid="contact-eyebrow">
              /// Book A Visit
            </span>
            <h2
              data-testid="contact-heading"
              className="mt-6 font-heading uppercase text-5xl sm:text-6xl lg:text-7xl leading-[0.9]"
            >
              Request an <br />
              <span className="text-[#FF1F1F]">appointment.</span>
            </h2>
            <p className="mt-6 text-lg text-black/80 leading-relaxed max-w-md">
              Tell us what you&apos;re after and when you&apos;d like to come by. We&apos;ll
              reply with a confirmed time slot.
            </p>

            <div className="mt-10 space-y-4">
              <InfoRow icon={MapPin} label="Location"  value={SHOP_INFO.address} testid="contact-info-location" />
              <InfoRow icon={Phone}  label="Phone"     value={SHOP_INFO.phone} testid="contact-info-phone" />
              <InfoRow icon={Mail}   label="Email"     value={SHOP_INFO.email} testid="contact-info-email" />
            </div>

            <div className="mt-8 nb-card p-5 bg-white" data-testid="contact-hours-card">
              <div className="flex items-center gap-2 mb-3">
                <Clock size={18} className="text-[#FF1F1F]" />
                <span className="font-mono text-xs uppercase tracking-widest font-bold">
                  Hours
                </span>
              </div>
              <ul className="space-y-2 text-sm">
                {SHOP_INFO.hours.map((h) => (
                  <li key={h.day} className="flex items-center justify-between gap-4">
                    <span className="font-heading uppercase text-lg">{h.day}</span>
                    <span className="font-mono text-xs uppercase tracking-widest text-black/70">
                      {h.time}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="lg:col-span-7">
            <form
              onSubmit={onSubmit}
              data-testid="contact-form"
              className="nb-card p-6 md:p-8 bg-white"
              noValidate
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="font-mono text-xs uppercase tracking-widest font-bold block mb-2">
                    Your Name
                  </label>
                  <input
                    data-testid="contact-input-name"
                    name="name"
                    value={form.name}
                    onChange={onChange}
                    placeholder="Misty Williams"
                    className="nb-input"
                    required
                  />
                </div>
                <div>
                  <label className="font-mono text-xs uppercase tracking-widest font-bold block mb-2">
                    Email
                  </label>
                  <input
                    data-testid="contact-input-email"
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={onChange}
                    placeholder="you@email.com"
                    className="nb-input"
                    required
                  />
                </div>
              </div>

              <div className="mt-5">
                <label className="font-mono text-xs uppercase tracking-widest font-bold block mb-2">
                  Phone (optional)
                </label>
                <input
                  data-testid="contact-input-phone"
                  name="phone"
                  value={form.phone}
                  onChange={onChange}
                  placeholder="(555) 010-7878"
                  className="nb-input"
                />
              </div>

              <div className="mt-5">
                <label className="font-mono text-xs uppercase tracking-widest font-bold block mb-2">
                  Message
                </label>
                <textarea
                  data-testid="contact-input-message"
                  name="message"
                  ref={messageRef}
                  rows={6}
                  value={form.message}
                  onChange={onChange}
                  placeholder="What you&apos;re looking for, preferred days/times, anything else we should know…"
                  className="nb-input"
                  required
                />
              </div>

              <div className="mt-7 flex flex-col sm:flex-row sm:items-center gap-4">
                <button
                  type="submit"
                  data-testid="contact-submit-button"
                  className="nb-btn-primary"
                  disabled={submitting}
                >
                  {submitting ? (
                    <>
                      <Loader2 className="animate-spin" size={20} /> Sending…
                    </>
                  ) : (
                    "Request Appointment"
                  )}
                </button>
                {done && (
                  <span
                    data-testid="contact-success-msg"
                    className="font-mono text-xs uppercase tracking-widest font-bold text-[#FF1F1F]"
                  >
                    Sent — we&apos;ll be in touch.
                  </span>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}

function InfoRow({ icon: Icon, label, value, testid }) {
  return (
    <div className="flex items-start gap-4" data-testid={testid}>
      <span className="w-12 h-12 inline-flex items-center justify-center bg-[#FF1F1F] border-4 border-black text-white shrink-0">
        <Icon size={20} />
      </span>
      <div className="min-w-0">
        <div className="font-mono text-[10px] uppercase tracking-widest font-bold text-black/60">
          {label}
        </div>
        <div className="font-heading uppercase text-lg sm:text-xl leading-tight break-words">{value}</div>
      </div>
    </div>
  );
}
